import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  ChangeSearchCriteriaByCatalogType,
  GetSavedSearchCriteriaByCatalogTypeFromStorage,
  SearchCriteriaInitialStateByCatalogType,
} from './catalog-search-criteria.actions';
import { TransportCatalogSearchCriteria } from '../monitoring/transport/models/transport.model';
import { TransportManagementSearchCriteria } from '../monitoring/transport-management/models/transport-management.model';
import { AutomotiveAftermarketSearchCriteria } from '../monitoring/automotive-aftermarket/models';
import { CatalogSearchCriteria } from '../../../common/models';
import { AllocationViewListSearchCriteria } from '../monitoring/allocation-view/models/allocation-view.model';
import { CatalogSearchCriteriaType } from './constants/catalog-search-criteria-type.constant';
import { CatalogSearchCriteriaService } from './catalog-search-criteria.service';

export interface CatalogSearchCriteriaStateModel {
  [CatalogSearchCriteriaType.Transport]: TransportCatalogSearchCriteria;
  [CatalogSearchCriteriaType.TransportManagement]: TransportManagementSearchCriteria;
  [CatalogSearchCriteriaType.AutomotiveAftermarket]: AutomotiveAftermarketSearchCriteria;
  [CatalogSearchCriteriaType.AllocationView]: AllocationViewListSearchCriteria;
}

const initialState: CatalogSearchCriteriaStateModel = {
  [CatalogSearchCriteriaType.Transport]: new TransportCatalogSearchCriteria(),
  [CatalogSearchCriteriaType.TransportManagement]: new TransportManagementSearchCriteria(),
  [CatalogSearchCriteriaType.AutomotiveAftermarket]: new AutomotiveAftermarketSearchCriteria(),
  [CatalogSearchCriteriaType.AllocationView]: new AllocationViewListSearchCriteria(),
};

@State<CatalogSearchCriteriaStateModel>({
  name: 'catalogSearchCriteria',
  defaults: initialState,
})
@Injectable()
export class CatalogSearchCriteriaState {
  constructor(private catalogSearchCriteriaService: CatalogSearchCriteriaService) {}

  @Selector()
  static getState(state: CatalogSearchCriteriaStateModel): CatalogSearchCriteriaStateModel {
    return state;
  }

  @Selector()
  static getSearchCriteriaByCatalogType(
    state: CatalogSearchCriteriaStateModel,
  ): (type: CatalogSearchCriteriaType) => CatalogSearchCriteria {
    return (type: CatalogSearchCriteriaType) => state[type];
  }

  @Action(ChangeSearchCriteriaByCatalogType)
  changeSearchCriteriaByCatalogType(
    ctx: StateContext<CatalogSearchCriteriaStateModel>,
    { payload: { searchCriteria, catalogSearchCriteriaType } }: ChangeSearchCriteriaByCatalogType,
  ): void {
    this.catalogSearchCriteriaService.setCatalogSearchCriteriaToStorage(catalogSearchCriteriaType, searchCriteria);

    ctx.patchState({
      [catalogSearchCriteriaType]: {
        ...ctx.getState()[catalogSearchCriteriaType],
        ...searchCriteria,
      },
    });
  }

  @Action(GetSavedSearchCriteriaByCatalogTypeFromStorage)
  getSavedSearchCriteriaByCatalogTypeFromStorage(
    ctx: StateContext<CatalogSearchCriteriaStateModel>,
    { payload }: GetSavedSearchCriteriaByCatalogTypeFromStorage,
  ): void {
    const searchCriteria = this.catalogSearchCriteriaService.getCatalogSearchCriteriaFromStorage(payload);

    if (searchCriteria) {
      ctx.patchState({
        [payload]: searchCriteria,
      });
    }
  }

  @Action(SearchCriteriaInitialStateByCatalogType)
  setInitialStateByCatalogType(
    ctx: StateContext<CatalogSearchCriteriaStateModel>,
    { payload }: SearchCriteriaInitialStateByCatalogType,
  ): void {
    ctx.patchState({
      [payload]: {
        ...initialState[payload],
      },
    });
  }
}
